import { normalizeToolName } from './tool-config.helpers';

export type ToolCategory =
  | 'code-execution'
  | 'intelligence'
  | 'end-test'
  | 'action';

export function getToolCategory(toolName: string): ToolCategory {
  const normalized = normalizeToolName(toolName);

  // End test tool
  if (normalized === 'end-test') {
    return 'end-test';
  }

  // Code execution tools
  if (
    normalized === 'coder' ||
    normalized === 'coder-tool' ||
    normalized === 'python' ||
    normalized === 'python-tool' ||
    normalized === 'sub-agent-coder' ||
    normalized === 'cli-tool' ||
    normalized === 'log-tool' ||
    normalized === 'remote-c2-command' ||
    normalized === 'mythic-send-task' ||
    normalized === 'mythic-get-task-output' ||
    normalized === 'password-audit-crack'
  ) {
    return 'code-execution';
  }

  // Intelligence/data-fetching tools (lightweight inline)
  if (
    normalized === 'sub-agent-intelligence' ||
    normalized === 'intelligence' ||
    normalized === 'ask-codebase' ||
    normalized === 'search-endpoints' ||
    normalized === 'get-endpoint-details' ||
    normalized === 'endpoint-details' ||
    normalized === 'endpoint-details-tool' ||
    normalized === 'endpoint-read' ||
    normalized === 'get-authentication-details' ||
    normalized === 'authentication-details' ||
    normalized === 'authentication-details-tool' ||
    normalized === 'get-mcp-authentication-details' ||
    normalized === 'mcp-authentication-details' ||
    normalized === 'mcp-connection-status-tool' ||
    normalized === 'mcp-test-connection-tool' ||
    normalized === 'websearch-tool' ||
    normalized === 'search-tracked-endpoints' ||
    normalized === 'list-assets-tool' ||
    normalized === 'get-tracked-endpoint-schema' ||
    normalized === 'get-webpages-from-endpoint' ||
    normalized === 'get-endpoints-from-webpage' ||
    normalized === 'list-endpoints-on-web-page' ||
    normalized === 'signals-semantic-search-tool' ||
    normalized === 'long-term-memory-tool' ||
    normalized === 'business-context-tool' ||
    normalized === 'get-business-context' ||
    normalized === 'application-scoping-tool' ||
    normalized === 'code-analyzer-tool' ||
    normalized === 'findings-tool' ||
    normalized === 'signal-latest-execution-summary-tool' ||
    normalized === 'signals-explorer-tool' ||
    normalized === 'findings-explorer-tool' ||
    normalized === 'endpoints-explorer-tool' ||
    normalized === 'executions-explorer-tool' ||
    normalized === 'har-explorer-tool' ||
    normalized === 'read-file' ||
    normalized === 'write-file' ||
    normalized === 'list-files' ||
    normalized === 'ls' ||
    normalized === 'list-ad-entities' ||
    normalized === 'get-ad-entity-details' ||
    normalized === 'get-ad-entity-relationships' ||
    normalized === 'query-ad-graph' ||
    normalized === 'create-internal-network-discovery-signal' ||
    normalized === 'get-existing-internal-network-discovery-signals' ||
    normalized === 'create-internal-network-scenario-draft' ||
    normalized === 'mythic-list-commands' ||
    normalized === 'mythic-list-callbacks' ||
    normalized === 'mythic-get-callback-info'
  ) {
    return 'intelligence';
  }

  if (toolName.toLowerCase().startsWith('mcp_')) {
    return 'intelligence';
  }

  // Memory tools
  if (normalized === 'get-neo-memories') {
    return 'action';
  }

  // Everything else is a generic action
  return 'action';
}

/**
 * Determines if a tool should be hidden in condensed view
 * Condensed view hides "noise" tools like thinking, todos, lookups
 * and only shows code executions, summaries, and errors
 */
export function shouldHideInCondensedView(toolName: string): boolean {
  const normalized = normalizeToolName(toolName);
  const category = getToolCategory(toolName);

  // Always show code execution tools
  if (category === 'code-execution') {
    return false;
  }

  // Always show end-test
  if (category === 'end-test') {
    return false;
  }

  // Hide thinking/intelligence tools
  if (
    normalized === 'sub-agent-intelligence' ||
    normalized === 'intelligence'
  ) {
    return true;
  }

  // Hide todo writes
  if (normalized === 'todo-write') {
    return true;
  }

  // Hide endpoint/credential lookups
  if (
    normalized === 'get-endpoint-details' ||
    normalized === 'endpoint-details' ||
    normalized === 'endpoint-details-tool' ||
    normalized === 'get-tracked-endpoint-schema' ||
    normalized === 'endpoint-read' ||
    normalized === 'get-authentication-details' ||
    normalized === 'authentication-details' ||
    normalized === 'authentication-details-tool' ||
    normalized === 'search-endpoints' ||
    normalized === 'search-tracked-endpoints'
  ) {
    return true;
  }

  // Hide web search tools
  if (normalized === 'websearch-tool' || normalized === 'navigate-web') {
    return true;
  }

  if (normalized === 'read-file' || normalized === 'grep') {
    return true;
  }

  // Hide other intelligence/lookup tools
  if (category === 'intelligence') {
    return true;
  }

  // Show everything else (actions, unknown tools)
  return false;
}
